import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton, { DATE_OPTION } from "src/components/ScoringChart/tool-utils/DateGroupButton";
import TitleTypography, { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserContext } from "src/context/config/userContext";
import {
  useUserMonitoringChartSelector,
  useUserMonitoringContext,
  useUserMonitoringSelector,
} from "src/context/monitoring/userMonitoringContext";
import { numberWithCommas, toFixed } from "src/services/utils";
import ButtonAddressTextField from "../ButtonAddressTextField";

const getOption = () => ({
  chart: {
    height: 500,
  },
  xAxis: {
    type: "datetime",
    gridLineWidth: 1,
  },
  ...beautifulTooltip((value) => `$${numberWithCommas(toFixed(value, 2))}`),
  legend: {
    enabled: true,
  },
});

function Note({ role }) {
  return (
    <NoteTypography>
      {role === "admin"
        ? "The chart shows the deposit and borrow of a user in a lending pool."
        : "The chart shows your deposit and borrow in a lending pool."}
    </NoteTypography>
  );
}

export default function AssetOverview() {
  const { pool } = useParams();
  const { role } = useUserContext();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { selectedAddress, setSelectedAddress } = useUserMonitoringContext();
  const { fetchAssetOverview } = useUserMonitoringSelector((state) => state.fetch);
  const assetOverview = useUserMonitoringChartSelector(MONITORING.USER.assetOverview, pool);

  useEffect(() => {
    if (assetOverview?.active === undefined) fetchAssetOverview(pool, { number: 90, interval: 8 }, { active: 2 });
    else fetchAssetOverview(pool, assetOverview?.metadata, { active: assetOverview?.active });
  }, [assetOverview?.active, pool, fetchAssetOverview]);

  const { isLoading, series, check } = useMemo(() => {
    if (assetOverview) {
      const check = assetOverview.deposit.length > 0 && assetOverview.borrow.length > 0;
      return {
        isLoading: false,
        series: [
          { name: "Deposit", data: [].concat(assetOverview.deposit) },
          { name: "Borrow", data: [].concat(assetOverview.borrow) },
        ],
        check: check,
      };
    }
    return {
      isLoading: true,
      series: [
        { name: "Deposit", data: [] },
        { name: "Borrow", data: [] },
      ],
      check: false,
    };
  }, [assetOverview]);

  return (
    <DetailMonitoringWrapper
      isAuthorization={true}
      navigation={{ text: "user monitoring", name: "asset overview", link: `/user-monitoring/${pool}` }}
      selectLink="/user-monitoring/asset-overview"
      Component={{ Description: <Note role={role} /> }}
    >
      {role === "admin" && (
        <ButtonAddressTextField
          defaultValue={selectedAddress}
          events={{ onConfirm: (value) => setSelectedAddress(value) }}
        />
      )}
      <Paper style={{ marginTop: "1rem" }}>
        <Box mb={2} display="flex" justifyContent="flex-end" alignItems="center">
          <DateGroupButton
            fullWidth={match}
            defaultActive={assetOverview?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchAssetOverview(pool, { from, to }, { active });
              },
            }}
            config={DATE_OPTION.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchAssetOverview(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        {check ? (
          <LineChart isLoading={isLoading} series={series} option={getOption()} />
        ) : role === "admin" ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="This account doesn't have any transactions" />
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <TitleTypography textProps={{ variant: "h4" }} title="You don't have any transactions" />
          </Box>
        )}
      </Paper>
    </DetailMonitoringWrapper>
  );
}
